import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { SCALES, INSTRUMENT_META, IQR_ITEMS, IEO_ITEMS, IPR_ITEMS } from '../data/instrumentsData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_MAP = { IQR: IQR_ITEMS, IEO: IEO_ITEMS, IPR: IPR_ITEMS };

const InstrumentSurvey = () => {
  const { instrumentType } = useParams();
  const navigate = useNavigate();

  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);
  const [windowId, setWindowId] = useState(null);
  const [moment, setMoment] = useState('T0');
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState('intro'); // intro | survey | done | error
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const type = instrumentType?.toUpperCase();
  const meta = INSTRUMENT_META[type];
  const items = ITEMS_MAP[type] || [];
  const scale = SCALES[type] || [];

  useEffect(() => {
    if (!meta) return;
    // Buscar janela ativa para este instrumento
    axios.get(`${API}/instruments/active`, { withCredentials: true })
      .then(res => {
        const active = res.data.instruments || [];
        const match = active.find(w => w.instrument_type === type);
        if (match) {
          setWindowId(match.window_id);
          setMoment(match.moment);
        } else {
          setPhase('error');
          setErrorMsg('Nenhuma janela de medição está aberta para você agora. Aguarde seu administrador abrir a janela de aplicação.');
        }
      })
      .catch(() => {
        setPhase('error');
        setErrorMsg('Erro ao verificar disponibilidade do instrumento.');
      });
  }, [type]);

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white p-4">
        <Card className="p-8 text-center max-w-md">
          <p className="text-gray-600">Instrumento não reconhecido.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Voltar ao Dashboard</Button>
        </Card>
      </div>
    );
  }

  const current = items[currentIdx];
  const total = items.length;
  const progressPct = Math.round((Object.keys(answers).length / total) * 100);
  const allAnswered = Object.keys(answers).length === total;

  const handleAnswer = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
    if (currentIdx < total - 1) {
      setTimeout(() => { if (mountedRef.current) setCurrentIdx(i => i + 1); }, 300);
    }
  };

  const handleSubmit = async () => {
    if (!allAnswered || !windowId) return;
    setIsSubmitting(true);
    try {
      const itemsPayload = {};
      items.forEach(item => {
        itemsPayload[item.id] = answers[item.id];
      });
      await axios.post(
        `${API}/instruments/respond/${type}`,
        { window_id: windowId, items: itemsPayload },
        { withCredentials: true }
      );
      setPhase('done');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao enviar respostas.';
      setErrorMsg(msg);
      setPhase('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Fase: erro ────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white p-4">
        <Card className="p-8 text-center max-w-md">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Instrumento indisponível</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{errorMsg}</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-purple-600 hover:bg-purple-700">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // ── Fase: concluído ───────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white p-4">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Obrigada!</h2>
          <p className="text-gray-600 mb-2">
            Suas respostas ao <strong>{meta.name}</strong> foram registradas com sucesso.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Momento: <strong>{moment}</strong>. Seus dados são anônimos e contribuem para o diagnóstico do time.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="bg-purple-600 hover:bg-purple-700">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // ── Fase: intro ───────────────────────────────────────────────────────────
  if (phase === 'intro') {
    const sectionLabels = meta.sections.map(s => s.label).join(' · ');
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8">
          <div className="mb-2">
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white mb-3"
              style={{ background: meta.color }}
            >
              {meta.abbr} · {moment}
            </span>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{meta.name}</h1>
            <p className="text-gray-600 mb-4">{meta.description}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm text-gray-600">
            <p><strong>{total} afirmações</strong> no total.</p>
            <p>Seções: {sectionLabels}.</p>
            <p>Não há respostas certas ou erradas. Responda com base na sua experiência real.</p>
            <p>Suas respostas são <strong>confidenciais</strong> — somente dados agregados da equipe são compartilhados com o gestor.</p>
          </div>
          <Button
            onClick={() => setPhase('survey')}
            className="w-full bg-purple-600 hover:bg-purple-700 py-3 text-base"
          >
            Começar
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      </div>
    );
  }

  // ── Fase: survey ──────────────────────────────────────────────────────────
  const prevSection = currentIdx > 0 ? items[currentIdx - 1]?.section : null;
  const sectionChanged = prevSection !== null && current.section !== prevSection;
  const sectionMeta = meta.sections[current.section === 'A' ? 0 : 1];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Sair
          </Button>
          <span className="text-sm font-medium text-gray-500">
            {currentIdx + 1} / {total}
          </span>
        </div>

        {/* Barra de progresso */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%`, background: meta.color }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{progressPct}% respondido</span>
            <span
              className="font-semibold"
              style={{ color: meta.color }}
            >
              {meta.abbr}
            </span>
          </div>
        </div>

        {/* Mudança de seção */}
        {sectionChanged && sectionMeta && (
          <div className="mb-4 p-3 bg-white border border-gray-200 rounded-xl text-center">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{sectionMeta.label}</p>
            <p className="text-sm text-gray-600 mt-1">{sectionMeta.intro}</p>
          </div>
        )}

        {/* Pergunta */}
        <Card className="p-6 mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: meta.color }}>
            {sectionMeta?.label}
          </p>
          <p className="text-lg font-medium text-gray-900 leading-relaxed mb-6">
            {current.text}
          </p>

          {/* Escala */}
          <div className="space-y-2">
            {scale.map(opt => {
              const selected = answers[current.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(current.id, opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition-all duration-150 flex items-center justify-between ${
                    selected
                      ? 'text-white font-medium'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                  style={selected ? { borderColor: meta.color, background: meta.color } : {}}
                >
                  <span>{opt.label}</span>
                  {selected && <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Navegação */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={currentIdx === 0}
            onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          {currentIdx < total - 1 ? (
            <Button
              disabled={answers[current.id] === undefined}
              onClick={() => setCurrentIdx(i => i + 1)}
              style={answers[current.id] !== undefined ? { background: meta.color } : {}}
              className="text-white"
            >
              Próxima
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              disabled={!allAnswered || isSubmitting}
              onClick={handleSubmit}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Enviando…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Concluir
                </>
              )}
            </Button>
          )}
        </div>

        {/* Mini-mapa de navegação */}
        <div className="mt-6 flex flex-wrap gap-1 justify-center">
          {items.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => setCurrentIdx(idx)}
              className={`w-5 h-5 rounded text-xs transition-colors ${
                idx === currentIdx
                  ? 'text-white'
                  : answers[item.id] !== undefined
                  ? 'bg-green-200 text-green-800'
                  : 'bg-gray-200 text-gray-400'
              }`}
              style={idx === currentIdx ? { background: meta.color } : {}}
              title={`Item ${item.id}`}
            >
              {item.id}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstrumentSurvey;
